import {getPayConfig} from '@/services/pay.service';
import globalStore from '@/services/global.state';
import {getBalance} from '@/services/global.service';
import {
  debounce,
  goTo,
  inGameBox,
  openURL,
  truncateToDecimalPlaces,
} from '@/utils';
import type {Success} from '@/components/utils/pay';
import {upiPayment} from '@/components/utils/pay';
import {
  createRechargeCore,
  createRechargeCryptoExtension,
  resolveInitialRechargeTabIdState,
  runRechargeBalanceRefresh,
  runRechargeProgressiveFetch,
  rechargeSortedBalanceIndices,
  extraBonusBalanceIndex,
  applyRechargeAmountPlaceholder,
  shouldSkipLangQueryForRechargeUrl,
  appendLangQuery,
  calculateTotalAmount,
  syncRechargePayMethodIdForCryptoMode,
  type RechargeProgressiveDataSource,
} from '@fe-progressive/package-progressive/recharge';
import {progressiveToastFail} from '@fe-progressive/package-progressive/progressive-toast';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {RouteProp, useFocusEffect, useRoute} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {getBalanceConfig} from './recharge-tab/recharge-tab.service';
import {getVipWeekCardList} from '../vip/vip.service';
import {
  getBalanceList,
  getPayMethod,
  goIncome,
  paySuccess,
} from './recharge.service';
import {useShallow} from 'zustand/react/shallow';
import {useRechargeCryptoUiStore, useRechargeStore} from './recharge.store';
import {createRechargeProgressiveAdapter} from './recharge-progressive.adapter';
import {
  parseExternalPayType,
  readExternalPayTypeRaw,
} from './util/recharge-external-pay-type';
const notToLanguageDomain = ['pay.wepaytm.xyz', 'pay.ptm-we.com'] as const;
const notToLanguagePayName = ['we pay'] as const;

const progressiveRechargeDataSource: RechargeProgressiveDataSource = {
  getVipWeekCardList,
  getBalanceList,
  getPayMethod,
  getBalanceConfig,
  getPayConfig,
};

export function useRechargeProgressiveCore() {
  const {i18n} = useTranslation();
  type RechargeRouteParams = {
    from?: string;
    cardId?: number;
    id?: string;
    payType?: string | number;
  };
  const route = useRoute<RouteProp<{params: RechargeRouteParams}>>();
  const fromWeekCard = useMemo(
    () => route.params?.from === 'weekCard',
    [route.params?.from],
  );
  const initWeekCardId = useMemo(
    () => route.params?.cardId,
    [route.params?.cardId],
  );

  const payTypeParamRaw = useMemo(
    () => readExternalPayTypeRaw(route.params),
    [route.params],
  );

  const {
    tabs,
    setActiveTabId,
    activeTabId,
    balanceList,
    balanceConfig,
    paymethodList,
    payMethodId,
    setPayMethodId,
    weekCardList,
    activeWeekCardId,
    setActiveWeekCardId,
    payConfig,
    applyRechargeFetchPatch,
    recomputeChannelDisabled,
  } = useRechargeStore(
    useShallow(s => ({
      tabs: s.tabs,
      setActiveTabId: s.setActiveTabId,
      activeTabId: s.activeTabId,
      balanceList: s.balanceList,
      balanceConfig: s.balanceConfig,
      paymethodList: s.paymethodList,
      payMethodId: s.payMethodId,
      setPayMethodId: s.setPayMethodId,
      weekCardList: s.weekCardList,
      activeWeekCardId: s.activeWeekCardId,
      setActiveWeekCardId: s.setActiveWeekCardId,
      payConfig: s.payConfig,
      applyRechargeFetchPatch: s.applyRechargeFetchPatch,
      recomputeChannelDisabled: s.recomputeChannelDisabled,
    })),
  );

  const {
    enableCrypto,
    setEnableCrypto,
    openDiskPayType,
    setOpenDiskPayType,
    openDiskPresetValue,
    setOpenDiskPresetValue,
  } = useRechargeCryptoUiStore(
    useShallow(s => ({
      enableCrypto: s.enableCrypto,
      setEnableCrypto: s.setEnableCrypto,
      openDiskPayType: s.openDiskPayType,
      setOpenDiskPayType: s.setOpenDiskPayType,
      openDiskPresetValue: s.openDiskPresetValue,
      setOpenDiskPresetValue: s.setOpenDiskPresetValue,
    })),
  );

  // 与 indusbet-main 一致：route.params.payType 优先，Web 回退 URL query，写入公共包 crypto UI store
  useLayoutEffect(() => {
    const parsed = parseExternalPayType(payTypeParamRaw);
    if (parsed) {
      setOpenDiskPayType(parsed);
    }
  }, [payTypeParamRaw, setOpenDiskPayType]);

  const activeTab = useMemo(
    () => tabs.find(tab => tab.id === activeTabId),
    [tabs, activeTabId],
  );

  const isWeekCard = useMemo(
    () => fromWeekCard || activeTabId === -2,
    [fromWeekCard, activeTabId],
  );

  const [loading, setLoadingState] = useState(true);
  const setLoading = useCallback((v: boolean) => {
    setLoadingState(v);
  }, []);

  const adapter = useMemo(
    () => createRechargeProgressiveAdapter({i18n, setLoading}),
    [i18n, setLoading],
  );

  const core = useMemo(() => {
    const c = createRechargeCore({
      adapter,
      features: {enableCryptoExtension: enableCrypto},
    });
    const ext = createRechargeCryptoExtension({
      features: {enableCryptoExtension: enableCrypto},
    });
    c.attachCryptoExtension(ext);
    return c;
  }, [adapter, enableCrypto]);

  const cryptoExt = useMemo(() => core.getCryptoExtension(), [core]);

  const useType = useMemo(
    () =>
      cryptoExt?.mapPayTypeToUseType(openDiskPayType) ??
      (openDiskPayType === 'CRYPTO' ? 2 : 1),
    [cryptoExt, openDiskPayType],
  );

  const payMethodItem = useMemo(
    () => paymethodList.find(p => p.id === payMethodId),
    [paymethodList, payMethodId],
  );

  const [balance, setBalance] = useState('');
  const [amount, setAmount] = useState(0);
  const [incomeInfo, setIncomeInfo] = useState({
    upiId: '',
    orderNo: '',
  });
  const holdLoadingForRedirectRef = useRef(false);

  const canShowBalanceList = useMemo(() => {
    return [...balanceList].sort((a, b) => a.balance - b.balance);
  }, [balanceList]);

  const displayBalance = useMemo(
    () =>
      core.utils.getDisplayBalanceForRecharge({
        openDiskPayType,
        isWeekCard,
        openDiskPresetValue,
        balance,
      }),
    [core.utils, openDiskPayType, isWeekCard, openDiskPresetValue, balance],
  );

  const balanceId = useMemo(
    () =>
      core.utils.resolveBalanceIdString({
        rows: canShowBalanceList.map(b => ({id: b.id, balance: b.balance})),
        openDiskPayType,
        isWeekCard,
        displayBalance,
        balance,
      }),
    [
      core.utils,
      canShowBalanceList,
      openDiskPayType,
      isWeekCard,
      displayBalance,
      balance,
    ],
  );

  useEffect(() => {
    if (openDiskPayType === 'CRYPTO' && balanceList.length > 0) {
      const preset = cryptoExt?.suggestCryptoPresetFromBalances(
        balanceList.map(b => b.balance),
      );
      if (preset != null) {
        setOpenDiskPresetValue(preset);
      }
    }
  }, [balanceList, openDiskPayType, cryptoExt, setOpenDiskPresetValue]);

  const sortedBalanceIndexList = useMemo(
    () => rechargeSortedBalanceIndices(canShowBalanceList),
    [canShowBalanceList],
  );

  const extraBounsBalanceIndex = useMemo(
    () =>
      extraBonusBalanceIndex(
        canShowBalanceList,
        sortedBalanceIndexList,
        displayBalance,
      ),
    [canShowBalanceList, sortedBalanceIndexList, displayBalance],
  );

  const fetchRechargeData = useCallback(() => {
    setLoading(true);
    runRechargeProgressiveFetch({
      ds: progressiveRechargeDataSource,
      isWeekCard,
      openDiskPayType,
      useType,
    })
      .then(patch => {
        applyRechargeFetchPatch(patch, {initWeekCardId: initWeekCardId});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    isWeekCard,
    openDiskPayType,
    useType,
    initWeekCardId,
    applyRechargeFetchPatch,
    setLoading,
  ]);

  useEffect(() => {
    fetchRechargeData();
  }, [fetchRechargeData]);

  useEffect(() => {
    setBalance((canShowBalanceList[0]?.balance || '') + '');
  }, [canShowBalanceList]);

  useEffect(() => {
    const card = weekCardList.find(_card => _card.cardId === activeWeekCardId);
    if (card) {
      setBalance(card.price + '');
    }
  }, [activeWeekCardId, weekCardList]);

  const refreshMainBalance = useCallback(
    (withLoading = false) => {
      if (!globalStore.token) {
        return;
      }
      if (withLoading) {
        setLoading(true);
      }
      return runRechargeBalanceRefresh({
        getBalance,
        onMainBalance: setAmount,
      }).finally(() => {
        if (withLoading) {
          setLoading(false);
        }
      });
    },
    [setLoading],
  );

  const handleRefresh = useCallback(() => {
    if (!globalStore.token) {
      goTo('Login', {backPage: 'Home'});
      return;
    }
    refreshMainBalance(true);
  }, [refreshMainBalance]);

  useEffect(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      if (token) {
        refreshMainBalance(false);
      }
    });
    return () => sub.unsubscribe();
  }, [refreshMainBalance]);

  useFocusEffect(
    useCallback(() => {
      refreshMainBalance(false);
    }, [refreshMainBalance]),
  );

  const channelValidationResult = useMemo(
    () =>
      debounce((selectedBalance: number) => {
        recomputeChannelDisabled(selectedBalance);
      }),
    [recomputeChannelDisabled],
  );

  useEffect(() => {
    channelValidationResult(+balance);
  }, [balance, channelValidationResult]);

  useEffect(() => {
    syncRechargePayMethodIdForCryptoMode();
  }, [openDiskPayType, paymethodList]);

  const onSuccess = (success: Success) => {
    if (success.status === 'SUCCESS') {
      paySuccess({
        orderNo: incomeInfo.orderNo,
        tradeResult: '1',
        approvalUrt: success.approvalRefNo,
      })
        .then()
        .finally(() => {
          globalStore.updateAmount.next({});
        });
    }
  };

  const onFailure = (error: Error) => {
    progressiveToastFail(error.message);
  };

  const handleRecharge = async () => {
    const {minAmount, maxAmount, payTag} = payMethodItem || {};
    if (
      !displayBalance ||
      +displayBalance <= 0 ||
      (minAmount && +displayBalance < minAmount) ||
      (maxAmount && +displayBalance > maxAmount)
    ) {
      progressiveToastFail(i18n.t('recharge-page.tip.money-error'));
      return;
    }
    if (!payMethodItem) {
      progressiveToastFail(i18n.t('recharge-page.tip.paymethod-error'));
      return;
    }
    setLoading(true);

    core.track({event: 'recharge_start'});
    core.track({
      event: 'rechargeClick',
      amount: displayBalance,
    });
    holdLoadingForRedirectRef.current = false;

    goIncome({
      balanceId: balanceId || 0,
      payTag: payMethodItem.payTag,
      payTypeId: payMethodId + '',
      rechargeBalance: balanceId ? 0 : displayBalance,
      ...(isWeekCard ? {cardId: activeWeekCardId} : {}),
      payCategory: useType,
    })
      .then(res => {
        core.track({
          event: 'recharge_request_finish',
          extra: {value: JSON.stringify(res)},
        });
        if (payTag === 'IM_RECHARGE') {
          holdLoadingForRedirectRef.current = true;
          Promise.resolve(
            adapter.onImRecharge?.({
              amount: balance,
              payTag: payMethodItem.payTag,
            }),
          ).catch(() => {});
          return;
        }
        if (typeof res === 'string') {
          let url = applyRechargeAmountPlaceholder(res, balance);

          if (
            !shouldSkipLangQueryForRechargeUrl({
              url,
              payNameLower: payMethodItem?.payName?.toLowerCase(),
              skipLangDomains: notToLanguageDomain,
              skipLangPayNameSubstrings: notToLanguagePayName,
            })
          ) {
            url = appendLangQuery(url, globalStore.lang);
          }

          holdLoadingForRedirectRef.current = true;
          if (globalStore.isWeb) {
            core.track({
              event: 'recharge_open',
              extra: {value: JSON.stringify({isWeb: true})},
            });
            if (inGameBox) {
              openURL(url);
              holdLoadingForRedirectRef.current = false;
              setLoading(false);
            } else {
              window.location.href = url;
            }
          } else {
            core.track({
              event: 'recharge_open',
              extra: {value: JSON.stringify({isWeb: false})},
            });
            goTo('WebView', {
              originUrl: url,
              header: true,
              headerTitle: i18n.t('label.recharge'),
              serverRight: true,
              hideAmount: true,
            });
          }
        } else {
          core.track({
            event: 'recharge_other',
            extra: {value: JSON.stringify(res)},
          });
          holdLoadingForRedirectRef.current = true;
          setIncomeInfo(res as {upiId: string; orderNo: string});
        }
      })
      .catch(e => {
        let error: unknown = e;
        if (typeof e === 'string') {
          error = e;
        } else if (e && typeof e === 'object' && 'message' in e) {
          error = (e as {message: string}).message;
        } else if (e && typeof e === 'object' && 'msg' in e) {
          const o = e as {code?: string; msg?: string};
          error = `${o.code}: ${o.msg}`;
        } else {
          try {
            error = JSON.stringify(e);
          } catch {
            core.track({
              event: 'recharge_request_unknown',
              extra: {value: navigator.userAgent},
            });
          }
        }
        core.track({
          event: 'recharge_request_error',
          extra: {value: `v3: ${error}`},
        });
        holdLoadingForRedirectRef.current = false;
        setLoading(false);
      })
      .finally(() => {
        core.track({event: 'recharge_request_complete'});
        if (!holdLoadingForRedirectRef.current) {
          setLoading(false);
        }
      });
  };

  const onPay = () => {
    const config = {
      payeeVpa: incomeInfo.upiId,
      payeeName: incomeInfo.upiId,
      merchantCode: incomeInfo.upiId,
      transactionId: incomeInfo.orderNo,
      transactionRefId: incomeInfo.orderNo,
      description: incomeInfo.orderNo,
      amount: balance + '',
    };
    upiPayment.initiate('net.one97.paytm', '', config, onSuccess, onFailure);
  };

  useEffect(() => {
    if (incomeInfo.orderNo && incomeInfo.upiId) {
      try {
        onPay();
      } finally {
        holdLoadingForRedirectRef.current = false;
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeInfo]);

  const routeTabAppliedRef = useRef(false);
  const tabId = useMemo(() => route.params?.id, [route.params?.id]);

  useEffect(() => {
    const r = resolveInitialRechargeTabIdState({
      tabs,
      fromWeekCard,
      routeTabId: tabId,
      routeTabApplied: routeTabAppliedRef.current,
      currentActiveTabId: useRechargeStore.getState().activeTabId,
    });
    if (r.markRouteTabConsumed) {
      routeTabAppliedRef.current = true;
    }
    if (typeof r.nextActiveTabId === 'number') {
      setActiveTabId(r.nextActiveTabId);
    }
  }, [tabs, fromWeekCard, tabId, setActiveTabId]);

  return {
    core,
    enableCrypto,
    setEnableCrypto,
    loading,
    i18n,
    isWeekCard,
    currencySymbol: globalStore.currency,
    openDiskPayType,
    setOpenDiskPayType,
    openDiskPresetValue,
    setOpenDiskPresetValue,
    payConfig,
    paymethodList,
    payMethodId,
    setPayMethodId,
    payMethodItem,
    balance,
    setBalance,
    amount,
    displayBalance,
    balanceId,
    canShowBalanceList,
    extraBounsBalanceIndex,
    activeTab,
    balanceConfig,
    useType,
    weekCardList,
    activeWeekCardID: activeWeekCardId,
    setActiveWeekCardID: setActiveWeekCardId,
    handleRefresh,
    handleRecharge,
    handleGotoRecords: () => {
      if (!globalStore.token) {
        goTo('Login', {backPage: 'Home'});
        return;
      }
      if (isWeekCard) {
        goTo('WeekCardBuyRecord');
      } else {
        goTo('RechargeRecords');
      }
    },
    truncateToDecimalPlaces,
    calculateTotalAmount,
  };
}

export type RechargeProgressiveVm = ReturnType<
  typeof useRechargeProgressiveCore
>;
